import React from 'react';
import { FOUNDING_PRICING_BADGE, PLANS, type PlanDef } from '../lib/pricing';

type PlanType = PlanDef['id'];

interface PricingSectionProps {
  onSelectPlan: (planType: PlanType) => void;
  currentPlan?: PlanType | null;
  /**
   * Which tiers to render. Defaults to the full ladder (`PLANS`) for the
   * authenticated plan switcher (BillingPage). Public-facing, unauthenticated
   * surfaces should pass `PUBLIC_PLANS` instead, so the hidden Site tier
   * never appears to a cold visitor.
   */
  plans?: PlanDef[];
}

const CheckIcon: React.FC = () => (
  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const PricingSection: React.FC<PricingSectionProps> = ({
  onSelectPlan,
  currentPlan,
  plans: planDefs = PLANS,
}) => {
  const plans = planDefs.map((p) => ({
    type: p.id,
    name: p.name,
    price: p.monthly,
    anchorMonthly: p.anchorMonthly,
    description: p.tagline,
    popular: p.popular,
    features: p.features,
  }));

  return (
    <div
      className={`grid grid-cols-1 gap-6 ${
        plans.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
      }`}
    >
      {plans.map((plan) => {
        const isCurrent = currentPlan === plan.type;
        return (
          <div
            key={plan.type}
            className={`relative bg-white rounded-xl border-2 p-6 flex flex-col ${
              plan.popular
                ? 'border-accent shadow-lg shadow-accent/10'
                : 'border-zinc-200'
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-accent text-white">
                  Most Popular
                </span>
              </div>
            )}

            <div className="mb-4">
              <h3 className="text-lg font-bold text-zinc-900">{plan.name}</h3>
              <p className="text-sm text-zinc-500 mt-1">{plan.description}</p>
            </div>

            <div className="mb-6">
              {plan.anchorMonthly && (
                <div className="mb-1 flex items-center gap-2">
                  <span className="font-mono text-sm text-zinc-400 line-through">
                    ${plan.anchorMonthly}/mo
                  </span>
                  <span className="inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
                    {FOUNDING_PRICING_BADGE}
                  </span>
                </div>
              )}
              <span className="text-4xl font-bold text-zinc-900">${plan.price}</span>
              <span className="text-zinc-500">/month</span>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-zinc-700">
                  <CheckIcon />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => onSelectPlan(plan.type)}
              disabled={isCurrent}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${
                isCurrent
                  ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                  : plan.popular
                  ? 'bg-primary hover:bg-ink-soft text-white'
                  : 'bg-zinc-900 hover:bg-zinc-800 text-white'
              }`}
            >
              {isCurrent ? 'Current Plan' : 'Get Started'}
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default PricingSection;
